import { Button } from '@/components/ui/Button';
import type { DateRangeValue } from '@/types/filters.types';
import {
  DATE_RANGE_PRESETS,
  isDateRangePresetKey,
  resolvePreset,
} from '@/lib/dateRangePresets';
import { useState, useEffect } from 'react';
import { DatePicker } from '@/components/ui/DatePicker';

interface FilterDateRangeProps {
  value: DateRangeValue;
  onChange: (v: DateRangeValue) => void;
  onClear?: () => void;
}

export function FilterDateRange({ value, onChange, onClear }: FilterDateRangeProps) {
  const [from, setFrom] = useState(value.from);
  const [to, setTo] = useState(value.to);

  useEffect(() => {
    setFrom(value.from);
    setTo(value.to);
  }, [value.from, value.to]);

  const handlePresetChange = (raw: string) => {
    if (!raw) {
      onChange({ from: value.from, to: value.to, preset: undefined });
      return;
    }
    if (!isDateRangePresetKey(raw)) return;
    const resolved = resolvePreset(raw);
    setFrom(resolved.from);
    setTo(resolved.to);
    onChange({ from: resolved.from, to: resolved.to, preset: raw });
  };

  const handleApply = () => {
    onChange({ from, to, preset: undefined });
  };

  const handleClear = () => {
    setFrom('');
    setTo('');
    if (onClear) onClear();
    else onChange({ from: '', to: '', preset: undefined });
  };

  const presetValue = value.preset ?? '';
  const hasValue = !!(value.from || value.to || value.preset);

  return (
    <div className="space-y-2">
      <div className="space-y-1.5">
        <label className="text-[10px] text-slate-500 uppercase tracking-wider">preset</label>
        <select
          value={presetValue}
          onChange={(e) => handlePresetChange(e.target.value)}
          className="h-8 w-full rounded-md border border-slate-200 bg-white px-2 text-xs text-slate-800 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        >
          <option value="">No Preset Selected</option>
          {DATE_RANGE_PRESETS.map((p) => (
            <option key={p.key} value={p.key}>
              {p.label}
            </option>
          ))}
        </select>
      </div>

      <div className="space-y-1.5">
        <label className="text-[10px] text-slate-500 uppercase tracking-wider">from</label>
        <DatePicker
          size="xs"
          ariaLabel="From date"
          value={from}
          onChange={setFrom}
        />
      </div>
      <div className="space-y-1.5">
        <label className="text-[10px] text-slate-500 uppercase tracking-wider">to</label>
        <DatePicker
          size="xs"
          ariaLabel="To date"
          value={to}
          onChange={setTo}
        />
      </div>
      <Button
        size="sm"
        className="w-full h-8 text-xs"
        onClick={handleApply}
      >
        Apply Date
      </Button>
      {hasValue && (
        <div className="flex justify-end">
          <button
            onClick={handleClear}
            className="text-[10px] text-blue-600 hover:underline"
          >
            clear
          </button>
        </div>
      )}
    </div>
  );
}
